/**
 * The tasks module's shipped catalogue, plus the fixtures the verification
 * suite runs on.
 *
 * {@link DEFAULT_TASK_TYPES} is the one thing here that is actually seeded: the
 * seven types the unit works with, shipped as `isSystem` records so they cannot
 * be deleted, while a type a manager adds from the creation form carries
 * neither flag.
 *
 * {@link MOCK_CONTACTS} and {@link buildMockTasks} are **not** seeded — the
 * board and the contacts book start empty on a fresh installation. They exist
 * so `healthcheck.js` has a realistic board to exercise the filters, the
 * assignment engine and the reports against; the tasks are built around a
 * supplied moment rather than hard-coded dates, so "היום" and "מחר" always land.
 */
import { toDateKey, shiftDateKey } from './task-filters';
import type { Contact } from '../types/contact';
import type { Task, TaskType } from '../types/task';

const SEEDED_AT = '2026-01-01T08:00:00.000Z';

/** The account the seeded board is filed under — the seeded super-admin. */
export const SEED_TASK_AUTHOR = 'usr-super-admin-01';

// ── Task types ───────────────────────────────────────────────────────

export const DEFAULT_TASK_TYPES: readonly TaskType[] = [
  {
    id: 'tt-drillers',
    name: 'דרילרים',
    description: 'שינוע צוותי דרילרים וציוד קידוח אל אתרי העבודה וחזרה.',
    isDefault: true,
    isSystem: true,
    createdAt: SEEDED_AT,
  },
  {
    id: 'tt-emulsion',
    name: 'אמולסיה',
    description: 'הובלת אמולסיה במכלית אל המאגרים ואתרי הסלילה.',
    isDefault: true,
    isSystem: true,
    createdAt: SEEDED_AT,
  },
  {
    id: 'tt-refuel',
    name: 'תדלוק',
    description: 'תדלוק כלים הנדסיים ורכבים בשטח ובמוצבים.',
    isDefault: true,
    isSystem: true,
    createdAt: SEEDED_AT,
  },
  {
    id: 'tt-equipment-transfer',
    name: 'שינוע כלי',
    description: 'העברת כלי הנדסי או רכב בין אתרים, לרוב על מוביל.',
    isDefault: true,
    isSystem: true,
    createdAt: SEEDED_AT,
  },
  {
    id: 'tt-scramble',
    name: 'הקפצה',
    description: 'יציאה מיידית של צוות או רכב לקריאה דחופה.',
    isDefault: true,
    isSystem: true,
    createdAt: SEEDED_AT,
  },
  {
    id: 'tt-admin',
    name: 'משימה מנהלתית',
    description: 'נסיעה מנהלתית — אספקה, דואר, ליווי ובקרות.',
    isDefault: true,
    isSystem: true,
    createdAt: SEEDED_AT,
  },
  {
    id: 'tt-other',
    name: 'משימה אחרת',
    description: 'משימה שאינה נכנסת לאף קטגוריה קיימת.',
    isDefault: true,
    isSystem: true,
    createdAt: SEEDED_AT,
  },
];

// ── Contacts book ────────────────────────────────────────────────────

export const MOCK_CONTACTS: readonly Contact[] = [
  {
    id: 'cnt-001',
    fullName: 'רס״ן דוד לוי',
    role: 'קצין אפסנאות',
    organization: 'אג״ם יחידה 99',
    primaryPhone: '0501112233',
    secondaryPhone: '0731112233',
    notes: 'איש הקשר לכל יציאה של דרילרים.',
    createdAt: SEEDED_AT,
  },
  {
    id: 'cnt-002',
    fullName: 'מירי כהן',
    role: 'מנהלת מחסן',
    organization: 'מחסן מרכזי — צריפין',
    primaryPhone: '0523334455',
    notes: 'זמינה בין 07:00 ל-16:00 בלבד.',
    createdAt: SEEDED_AT,
  },
  {
    id: 'cnt-003',
    fullName: 'סמ״ר יוסי אברהם',
    role: 'מפקד צוות',
    organization: 'פלוגת שינוע 99',
    primaryPhone: '0545556677',
    createdAt: SEEDED_AT,
  },
];

// ── Tasks ────────────────────────────────────────────────────────────

/** The seeded board, laid out around `now`. */
export function buildMockTasks(now: number = Date.now(), createdBy: string = SEED_TASK_AUTHOR): Task[] {
  const stamp = new Date(now).toISOString();
  const today = toDateKey(now);
  const tomorrow = shiftDateKey(now, 1);
  const laterThisWeek = shiftDateKey(now, 3);

  const base = { createdBy, createdAt: stamp, updatedAt: stamp } as const;

  return [
    {
      ...base,
      id: 'tsk-seed-01',
      title: 'שינוע דרילר לאתר הצפוני',
      taskTypeId: 'tt-drillers',
      description: 'העמסת דרילר וצוות מהמוצב אל אתר הקידוח, המתנה בשטח וחזרה.',
      date: today,
      startTime: '06:00',
      estEndTime: '11:30',
      priority: 'high',
      contactId: 'cnt-001',
      destinationAddress: 'אתר קידוח צפוני, כביש 899',
      destinationLink: 'https://waze.com/ul?q=%D7%9B%D7%91%D7%99%D7%A9%20899',
      statusId: 'in_progress',
      operationalNotes: 'להצטייד ברתמות ובשרשראות קשירה נוספות.',
      internalNotes: 'הלקוח ביקש דיווח טלפוני למפקד היחידה בסיום.',
    },
    {
      ...base,
      id: 'tsk-seed-02',
      title: 'תדלוק כלים במוצב מערב',
      taskTypeId: 'tt-refuel',
      description: 'סבב תדלוק לכלים ההנדסיים שבמוצב.',
      date: today,
      startTime: '13:00',
      estEndTime: '15:00',
      priority: 'urgent',
      manualContact: { name: 'רס״ל אבי נחום', phone: '0509998877', role: 'מוקדן מוצב' },
      destinationAddress: 'מוצב מערב — שער הכלים',
      statusId: 'in_progress',
      operationalNotes: 'הכניסה למוצב דרך שער דרומי בלבד.',
    },
    {
      ...base,
      id: 'tsk-seed-03',
      title: 'הובלת אמולסיה למאגר הדרומי',
      taskTypeId: 'tt-emulsion',
      description: 'מילוי מכלית והובלה למאגר, פריקה בעמדת השאיבה.',
      date: tomorrow,
      startTime: '08:00',
      estEndTime: '12:00',
      priority: 'medium',
      contactId: 'cnt-002',
      destinationAddress: 'מאגר דרומי, אזור תעשייה',
      statusId: 'in_progress',
      internalNotes: 'לוודא מול המחסן שהמכסה החודשית לא נוצלה.',
    },
    {
      ...base,
      id: 'tsk-seed-04',
      title: 'איסוף ציוד מהמחסן המרכזי',
      taskTypeId: 'tt-admin',
      description: 'נסיעה מנהלתית לאיסוף ציוד שהוזמן עבור הפלוגה.',
      date: laterThisWeek,
      startTime: '09:00',
      estEndTime: '10:30',
      priority: 'low',
      contactId: 'cnt-003',
      destinationAddress: 'מחסן מרכזי — צריפין',
      statusId: 'in_progress',
    },
  ];
}
